<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.notification_create') }}
      <template v-slot:right>
        <router-link :to="{ name: 'user-notification-list'}"><b-button size="sm" variant="outline-primary">{{ $t('btn.notification.back_to_notification_list') }}</b-button></router-link>
      </template>
    </page-header>

    <page-card>
      <template v-slot:header>
        {{ $t('page.notification.name') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-12">
              <b-form-group>
                <b-form-input
                    id="input-2"
                    v-model="form.name"
                    required
                ></b-form-input>
              </b-form-group>
              <p v-if="$v.form.name.$dirty && $v.form.name.$invalid" class="text-danger">{{ $t('error.required') }}</p>
            </div>
          </div>
        </div>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.notification.target') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="name"
                  :label="$t('filters.label.notification.segment.name')"
                  :placeholder="$t('filters.placeholder.notification.segment.name')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="createdAt"
                  open-direction="left"
                  :label="$t('filters.notification.createdAt')">
              </filter-date-range-picker>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-12 d-flex justify-content-end">
              <b-button class="mr-2" @click="resetFilters()" variant="secondary">{{ $t('filters.btn.clear') }}</b-button>
              <b-button @click="applyFilters()" variant="primary">{{ $t('filters.btn.apply') }}</b-button>
            </div>
          </div>

          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
          <div class="table-responsive">
            <b-table striped
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-desc.sync="orderDirection"
                     :empty-text="$t('table.no_results')"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template #cell(id)="data">
                <div class="d-flex align-items-center">
                  <b-checkbox v-model="form.segmentIds"
                              :key="data.value"
                              :value="data.value">
                  </b-checkbox>
                  {{ data.item.id | shortId }}
                </div>
              </template>

              <template #cell(createdAt)="data">
                {{ data.item.createdAt | dateTime24HoursFormat }}
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
        </div>
        <p v-if="$v.form.segmentIds.$dirty && $v.form.segmentIds.$invalid" class="text-danger mt-3">{{ $t('error.required') }}</p>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.notification.content') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-12">
              <editor
                  :apiKey="editorApiKey"
                  :init="{
                    height: 500,
                    plugins: editorPlugins,
                    toolbar: editorToolbar
                  }"
                  v-model="form.content"
              />
            </div>
          </div>
          <p v-if="$v.form.content.$dirty && $v.form.content.$invalid" class="text-danger mt-3">{{ $t('error.required') }}</p>
        </div>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.notification.display_options') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid sygni-grid--narrow-date-picker">
          <div class="row">
            <div class="col-3">
              <b-form-group
                  :label="$t('notification.form.is_approval_required')"
                  label-for="displayDate"
              >
                <b-form-select
                    style="height: 43px;"
                    v-model="form.isApprovalRequired"
                    :options="isApprovalOptions">
                </b-form-select>
              </b-form-group>
              <p v-if="$v.form.isApprovalRequired.$dirty && $v.form.isApprovalRequired.$invalid" class="text-danger">{{ $t('error.required') }}</p>
            </div>
            <div class="col-3">
              <b-form-group
                  :label="$t('notification.form.display_date_from')"
                  label-for="displayDate"
              >
                <datetime
                    v-model="form.displayDateFrom"
                    type="datetime"
                    input-class="form-control"
                    value-zone="UTC"
                    zone="UTC"
                    format="yyyy-MM-dd HH:mm:ss"
                    :phrases="{ok: $t('notification.btn.date_time.continue'), cancel: $t('notification.btn.date_time.exit')}"
                    :hour-step="1"
                    :minute-step="1"
                    :week-start="7"
                    auto
                ></datetime>
                <p v-if="$v.form.displayDateFrom.$dirty && $v.form.displayDateFrom.$invalid" class="text-danger">{{ $t('error.required') }}</p>
              </b-form-group>
            </div>
            <div class="col-3">
              <b-form-group
                  :label="$t('notification.form.display_date_to')"
                  label-for="displayDate"
              >
                <datetime
                    v-model="form.displayDateTo"
                    type="datetime"
                    input-class="form-control"
                    value-zone="UTC"
                    zone="UTC"
                    format="yyyy-MM-dd HH:mm:ss"
                    :phrases="{ok: $t('notification.btn.date_time.continue'), cancel: $t('notification.btn.date_time.exit')}"
                    :hour-step="1"
                    :minute-step="1"
                    :week-start="7"
                    auto
                ></datetime>
              </b-form-group>
            </div>
            <div class="col-3">
              <b-form-group
                  :label="$t('notification.form.status')"
                  label-for="displayDate"
              >
                <b-form-select
                    style="height: 43px;"
                    v-model="form.status"
                    :options="statusOptions">
                </b-form-select>
              </b-form-group>
              <p v-if="$v.form.status.$dirty && $v.form.status.$invalid" class="text-danger">{{ $t('error.required') }}</p>
            </div>
          </div>

          <div class="row mt-5">
            <div class="col-12">
              <div class="d-flex justify-content-center w-100">
                <b-button @click="goToNotificationList" type="button" variant="secondary" class="w-25 mr-2">{{$t('btn.notification.cancel')}}</b-button>
                <b-button @click="onSubmit" type="button" variant="primary" class="w-25">{{$t('btn.notification.save')}}</b-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </page-card>
  </div>
</template>

<script>
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";
import { Datetime } from 'vue-datetime';
import {required} from "vuelidate/lib/validators";
import Editor from "@tinymce/tinymce-vue";
import moment from "moment";
import alertify from "alertifyjs";

//TODO: import / load / get locale the right way
let locale = locale ? locale : "en";

import {Settings} from 'luxon'
Settings.defaultLocale = locale ? locale : "en";

export default {
  props: ['apiKey'],
  components: {
    PageCard,
    PageHeader,
    GridPagination,
    FilterInput,
    FilterDateRangePicker,
    datetime: Datetime,
    'editor': Editor
  },
  data() {
    return {
      perPage: 10,
      currentPage: 1,
      selectedRows: [],
      orderDirection: '',
      campaignId: null,
      actionType: '',
      notificationId: null,
      isApprovalOptions: [
        {
          text: this.$t('notification.option.is_approval'),
          value: true
        },
        {
          text: this.$t('notification.option.is_not_approval'),
          value: false
        }
      ],
      statusOptions: [
        {
          text: this.$t('notification.option.draft'),
          value: 'draft'
        },
        {
          text: this.$t('notification.option.active'),
          value: 'active'
        }
      ],
      form: {
        name: '',
        content: '',
        segmentIds: [],
        displayDateFrom: '',
        displayDateTo: '',
        isApprovalRequired: null,
        status: ''
      },
      buttonStatus: null,
      filterQuery: [],
      fields: [
        {key: 'id', label: this.$t('table.header.notification.segment_id')},
        {key: 'name', label: this.$t('table.header.notification.segment_name'), sortable: false},
        {key: 'createdAt', label: this.$t('table.header.notification.segment_created_at'), sortable: false},
        {key: 'playersCount', label: this.$t('table.header.notification.segment_players_count'), sortable: false}
      ],
      editorApiKey: '2a3y3y6ckc55ceyj8yvqoicrwnruvsf32dtctkkgop19qea3',
      editorPlugins: [
        'advlist autolink lists link image charmap',
        'searchreplace visualblocks code fullscreen',
        'print preview anchor insertdatetime media',
        'paste code help wordcount table', 'code'
      ],
      editorToolbar: 'undo redo | formatselect | bold italic | \
        alignleft aligncenter alignright | \
        code bullist numlist outdent indent | help'
    };
  },
  validations: {
    form: {
      name: {
        required
      },
      content: {
        required
      },
      segmentIds: {
        required
      },
      displayDateFrom: {
        required
      },
      isApprovalRequired: {
        required
      },
      status: {
        required
      }
    }
  },
  computed: {
    isBusy: function () {
      return this.$store.getters["userNotification/isBusy"];
    },
    items: function () {
      return this.$store.getters["userNotification/items"];
    },
    totalCount: function () {
      return this.$store.getters["userNotification/totalCount"];
    }
  },
  mounted() {
    this.loadItems();

    if (this.$route.params && this.$route.params.id) {
      this.notificationId = this.$route.params.id;
      this.actionType = this.$route.params.actionType;
      this.campaignId = this.$route.params.id;
      this.form.name = this.$route.params.name;
      this.form.content = this.$route.params.content;
      this.form.status = this.$route.params.status;
      this.form.displayDateFrom = this.$route.params.displayDateFrom;
      this.form.displayDateTo = this.$route.params.displayDateTo;
      this.form.isApprovalRequired = this.$route.params.isApprovalRequired;
      this.form.segmentIds = this.$route.params.segments.map(segment => {
        return segment.id;
      });
    }
  },
  methods: {
    applyFilters: function () {
      this.currentPage = 1;
      this.loadItems();
    },
    resetFilters() {
      this.$root.$emit('resetFilters');
      this.filterQuery = [];
      this.applyFilters();
    },
    onSubmit() {
      if(!this.$v.$invalid) {
        const payload = {
          ...this.form,
          id: this.notificationId,
          actionType: this.actionType,
          displayDateFrom: this.form.displayDateFrom ? moment(this.form.displayDateFrom).utc().format('YYYY-MM-DD HH:mm:ss') : null,
          displayDateTo: this.form.displayDateTo ? moment(this.form.displayDateTo).utc().format('YYYY-MM-DD HH:mm:ss') : null
        }

        this.$store.dispatch('userNotification/saveNotification', payload).then(() => {
          this.goToNotificationList();
        }).catch(() => {
          alertify.error(this.$t('notification.save_notification_error'));
        });
      } else {
        this.$v.$touch();
      }
    },
    goToNotificationList() {
      this.$router.push({ path: `user-notification-list` });
    },
    setIsApprovalRequired(value) {
      this.form.isApprovalRequired = value;
    },
    loadItems() {
      this.$store.dispatch('userNotification/loadSegments', {
        params: {
          offset: this.perPage * (this.currentPage - 1),
          limit: this.perPage
        },
        filters: this.filterQuery,
        sort: this.createSortQuery(),
        id: this.$route.params.id
      });
    },
    paginationReload(data) {
      this.currentPage = data.currentPage;
      this.perPage = data.perPage;
      this.loadItems();
    },
    // backToSegmentList() {
    //   this.$router.push({ name: 'user-segment-list' });
    // },
    createSortQuery() {
      // let sorting;
      // if (this.orderColumn) {
      //   if (this.orderDirection === true) {
      //     sorting = encodeURIComponent('+' + this.orderColumn);
      //   } else {
      //     sorting = encodeURIComponent('-' + this.orderColumn);
      //   }
      //   return '&sort[]=' + sorting;
      // }
      return '';
    }
  }
}
</script>
